import type { Screen } from '@ghostscreen/contracts';
import { i18n } from '../../i18n';
import { getCardSyncStatus, type CardSyncStatus } from '../../lib/dashboard/screenStatus';

/** How long after SYNC_NOW `executed` we wait for reported version catch-up. */
export const SYNC_NOW_VERSION_CATCHUP_MS = 20_000;

export function formatPlaylistVersion(version: number | null | undefined): string {
  if (version == null || !Number.isInteger(version) || version < 0) return '—';
  return `v${version}`;
}

export type ContentSyncGuidance = {
  status: CardSyncStatus;
  desiredLabel: string;
  reportedLabel: string;
  /** Operator-facing explanation for the current sync state. */
  message: string;
  /** When true, offer Sync now (player has a linked device and content may need a pull). */
  canSyncNow: boolean;
};

/**
 * Copy + affordances for the Manage panel content-sync card.
 * Keeps presentation rules out of the React tree.
 */
export function getContentSyncGuidance(
  screen: Screen,
  options: { unpaired: boolean; hasUnpublishedDraft: boolean },
): ContentSyncGuidance {
  const status = getCardSyncStatus(screen);
  const desiredLabel = formatPlaylistVersion(screen.desiredPlaylistVersion);
  const reportedLabel = formatPlaylistVersion(screen.reportedPlaylistVersion);
  const paired = !options.unpaired && Boolean(screen.deviceId);

  if (!paired) {
    return {
      status: 'unknown',
      desiredLabel,
      reportedLabel,
      message: i18n.t('displays:contentSync.guidance.unpaired'),
      canSyncNow: false,
    };
  }

  if (options.hasUnpublishedDraft) {
    return {
      status,
      desiredLabel,
      reportedLabel,
      message: i18n.t('displays:contentSync.guidance.draft'),
      canSyncNow: true,
    };
  }

  if (status === 'outdated') {
    return {
      status,
      desiredLabel,
      reportedLabel,
      message: i18n.t('displays:contentSync.guidance.outdated'),
      canSyncNow: true,
    };
  }

  if (status === 'up-to-date') {
    return {
      status,
      desiredLabel,
      reportedLabel,
      message: i18n.t('displays:contentSync.guidance.upToDate'),
      canSyncNow: true,
    };
  }

  return {
    status,
    desiredLabel,
    reportedLabel,
    message: i18n.t('displays:contentSync.guidance.waiting'),
    canSyncNow: true,
  };
}

export type SyncNowVersionOutcome = {
  tone: 'ok' | 'bad' | 'warn' | 'pending';
  message: string;
};

/**
 * After SYNC_NOW reaches a terminal device status, explain whether the
 * screen version catch-up actually happened.
 *
 * `executedAtMs` + `nowMs` implement a grace window: players often report the
 * new playlist version on the heartbeat *after* the command is marked executed.
 */
export function getSyncNowVersionOutcome(
  screen: Screen,
  commandStatus: 'executed' | 'failed' | 'expired',
  timing?: { executedAtMs: number; nowMs: number },
): SyncNowVersionOutcome | null {
  if (commandStatus === 'failed') {
    return { tone: 'bad', message: i18n.t('displays:contentSync.outcome.failed') };
  }
  if (commandStatus === 'expired') {
    return { tone: 'warn', message: i18n.t('displays:contentSync.outcome.expired') };
  }

  const cardStatus = getCardSyncStatus(screen);
  if (cardStatus === 'up-to-date') {
    return { tone: 'ok', message: i18n.t('displays:contentSync.outcome.confirmed') };
  }

  const executedAtMs = timing?.executedAtMs;
  const nowMs = timing?.nowMs ?? Date.now();
  if (executedAtMs != null && nowMs - executedAtMs < SYNC_NOW_VERSION_CATCHUP_MS) {
    return { tone: 'pending', message: i18n.t('displays:contentSync.outcome.pending') };
  }

  return { tone: 'warn', message: i18n.t('displays:contentSync.outcome.stillOlder') };
}

/** True while we should keep refreshing screen versions after SYNC_NOW executed. */
export function shouldPollScreensAfterSyncExecuted(
  timing: { executedAtMs: number; nowMs: number },
): boolean {
  return timing.nowMs - timing.executedAtMs < SYNC_NOW_VERSION_CATCHUP_MS;
}
